import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import AiAnalyticsApi from '../../../../api/ai/AiAnalyticsApi';
import { MANAGE_AI_SEMANTIC_MODEL } from '../../../../constants/userOperations';
import { userHasAccessTo } from '../../../../services/auth';

import { isQueryRow, QUERY_COLUMNS, SCHEMA_COLUMNS, SCHEMA_GRID_INITIAL_STATE } from './semanticModelGrid';

export const TABLES = 'tables';
export const QUERIES = 'queries';

const EMPTY_DRAFT = { question: '', sql: '', description: '' };

export default function useSemanticModel() {
  const canManageSemanticModel = userHasAccessTo(MANAGE_AI_SEMANTIC_MODEL);
  const [rows, setRows] = useState([]);
  const [deployable, setDeployable] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [deploying, setDeploying] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(function trackUnmount() {
    return function markUnmounted() {
      isMountedRef.current = false;
    };
  }, []);

  const applyModel = useCallback(function(data) {
    setRows(data.rows || []);
    setDeployable(!data.deployed);
    setPendingCount(data.pendingCount || 0);
  }, []);

  const load = useCallback(
    async function() {
      setLoading(true);
      try {
        const response = await AiAnalyticsApi.getSemanticModel();
        applyModel(response.data);
        setError(null);
      } catch (e) {
        setError('Could not load the semantic model. This page requires administrator access.');
      } finally {
        setLoading(false);
      }
    },
    [applyModel]
  );

  useEffect(
    function loadModelWhenAuthorized() {
      if (canManageSemanticModel) {
        load();
      }
    },
    [canManageSemanticModel, load]
  );

  const processRowUpdate = useCallback(async function(newRow, oldRow) {
    const query = isQueryRow(newRow);
    const changed = query
      ? newRow.question !== oldRow.question || newRow.sql !== oldRow.sql
      : newRow.text !== oldRow.text;
    if (!changed) {
      return oldRow;
    }
    const response = query
      ? await AiAnalyticsApi.updateExampleQuery(newRow.id, {
          question: newRow.question,
          sql: newRow.sql,
          description: newRow.description
        })
      : await AiAnalyticsApi.updateSemanticRow(newRow.id, newRow.text);
    setPendingCount(count => (oldRow.pending ? count : count + 1));
    setDeployable(true);
    return response.data;
  }, []);

  const openAddDialog = useCallback(function() {
    setDraft(EMPTY_DRAFT);
    setAddOpen(true);
  }, []);

  const closeAddDialog = useCallback(function() {
    setAddOpen(false);
  }, []);

  const handleCreateExample = useCallback(
    async function() {
      setSaving(true);
      try {
        const response = await AiAnalyticsApi.createExampleQuery(draft);
        setRows(prev => [...prev, response.data]);
        setPendingCount(count => count + 1);
        setDeployable(true);
        setAddOpen(false);
        setError(null);
      } catch (e) {
        setError('Could not add the example query.');
      } finally {
        setSaving(false);
      }
    },
    [draft]
  );

  const handleDeploy = useCallback(
    async function() {
      if (deploying) {
        return;
      }
      setDeploying(true);
      try {
        const response = await AiAnalyticsApi.deploySchema();
        if (isMountedRef.current) {
          applyModel(response.data);
          setError(null);
        }
      } catch (e) {
        if (isMountedRef.current) {
          setError('Deploy failed. Please try again.');
        }
      } finally {
        if (isMountedRef.current) {
          setDeploying(false);
        }
      }
    },
    [applyModel, deploying]
  );

  const handleRowUpdateError = useCallback(function() {
    setError('Could not save the edit.');
  }, []);

  const queryRows = useMemo(() => rows.filter(isQueryRow), [rows]);
  const schemaRows = useMemo(() => rows.filter(row => !isQueryRow(row)), [rows]);
  const tablesCount = useMemo(() => rows.filter(row => row.kind === 'table').length, [rows]);

  const grid =
    selected === TABLES
      ? { rows: schemaRows, columns: SCHEMA_COLUMNS, initialState: SCHEMA_GRID_INITIAL_STATE }
      : { rows: queryRows, columns: QUERY_COLUMNS, initialState: undefined };

  return {
    canManageSemanticModel,
    loading,
    deploying,
    error,
    deployable,
    pendingCount,
    selected,
    setSelected,
    addOpen,
    openAddDialog,
    closeAddDialog,
    draft,
    setDraft,
    saving,
    queryRows,
    tablesCount,
    grid,
    processRowUpdate,
    handleRowUpdateError,
    handleCreateExample,
    handleDeploy
  };
}
